import React from "react";
import { useTranslation } from "react-i18next";
import { FormValuesFilters } from "../types";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import { InputWrapper } from "@/components/FormInputs/InputWrapper";
import {
  Interest,
  Major,
  User,
  MessagingYesNoEnum,
} from "@/__generated__/graphql";
import { i18n } from "@/i18n";

interface FilterReviewFieldsProps {
  filters: FormValuesFilters;
  errors?: Merge<FieldError, FieldErrorsImpl<FormValuesFilters>>;
  counselors?: Partial<User>[];
  majors?: Partial<Major>[];
  interests?: Partial<Interest>[];
}

export function FilterReviewFields({
  filters,
  errors,
  counselors,
  majors,
  interests,
}: FilterReviewFieldsProps) {
  const { t } = useTranslation();

  const {
    assignedCounselors: assignedCounselorIds,
    commitIndex,
    firstTimeMessageRecipients,
    funnelStates,
    interests: interestIds,
    location,
    majors: majorIds,
    startTerm,
    studentSource,
    studentType,
    userIds,
  } = filters;

  if (userIds?.length) {
    return (
      <div className="grid w-full grid-cols-3 gap-8">
        <FirstTimeMessageRecipient
          value={firstTimeMessageRecipients}
          error={errors?.firstTimeMessageRecipients}
        />
      </div>
    );
  }

  return (
    <div className="grid w-full grid-cols-3 gap-8">
      <InputWrapper
        label={t("filters.assignedCounselors")}
        error={errors?.assignedCounselors}
      >
        <FilterDisplay
          value={assignedCounselorIds
            ?.map((id) => counselors?.find((c) => c?.id === id))
            ?.map((counselor) =>
              counselor
                ? `${counselor.firstName} ${counselor.lastName}`
                : t("bulkMessaging.review.formerOfficial"),
            )}
        />
      </InputWrapper>
      <InputWrapper
        label={t("filters.studentType")}
        error={errors?.studentType}
      >
        <FilterDisplay value={translateValues(studentType)} />
      </InputWrapper>
      <InputWrapper
        label={t("filters.funnelState")}
        error={errors?.funnelStates}
      >
        <FilterDisplay value={translateFunnels(funnelStates)} />
      </InputWrapper>
      <InputWrapper label={t("filters.entryTerm")} error={errors?.startTerm}>
        <FilterDisplay
          value={startTerm?.map(
            ({ semester, year }) => `${translateValue(semester)} ${year}`,
          )}
        />
      </InputWrapper>
      <InputWrapper label={t("filters.location")} error={errors?.location}>
        <FilterDisplay value={translateValues(location)} />
      </InputWrapper>
      <InputWrapper label={t("filters.major")} error={errors?.majors}>
        <FilterDisplay
          value={majorIds
            ?.map((id) => majors?.find((c) => c?.id === id))
            ?.map((major) => major?.name || "-")}
        />
      </InputWrapper>
      <InputWrapper label={t("filters.interests")} error={errors?.interests}>
        <FilterDisplay
          value={interestIds
            ?.map((id) => interests?.find((c) => c?.id === id))
            ?.map((interest) => interest?.name || "-")}
        />
      </InputWrapper>
      <InputWrapper
        label={t("filters.studentSource")}
        error={errors?.studentSource}
      >
        <FilterDisplay value={translateValues(studentSource)} />
      </InputWrapper>
      <InputWrapper
        label={t("filters.commitIndex")}
        error={errors?.commitIndex}
      >
        <FilterDisplay value={translateValues(commitIndex)} />
      </InputWrapper>
      <FirstTimeMessageRecipient
        value={firstTimeMessageRecipients}
        error={errors?.firstTimeMessageRecipients}
      />
    </div>
  );
}

function FirstTimeMessageRecipient({
  value,
  error,
}: {
  value?: MessagingYesNoEnum;
  error?: FieldError;
}) {
  const { t } = useTranslation();

  return (
    <InputWrapper
      label={t("bulkMessaging.firstTimeMessageRecipients")}
      error={error}
    >
      <FilterDisplay value={value ? translateValue(value) : "-"} />
    </InputWrapper>
  );
}

function FilterDisplay({ value }: { value?: string | string[] }) {
  return <p className="text-sm text-muted-foreground">{filterText(value)}</p>;
}

function filterText(value?: string | string[]) {
  if (Array.isArray(value)) {
    return value?.length ? value.join(", ") : "-";
  } else {
    return value ? value : "-";
  }
}

function translateFunnels(values?: string[]) {
  return values?.map((value) => i18n.t(`funnelGroups.${value}`));
}

function translateValue(value: string) {
  return i18n.t(`filters.fieldOptions.${value}`);
}

function translateValues(values?: string[]) {
  return values?.map(translateValue);
}
